import React, { useState } from 'react';
import { useFormState } from 'react-final-form';
import { BsPencilFill } from 'react-icons/bs';
import { Tooltip } from '@bay-email/editor';
import { getMergeTagLabel } from '@bay-email/core';
import { SubjectPreviewDrawer } from '../SubjectPreviewDrawer';
import styles from './index.module.scss';

interface TextPart {
	type: 'text' | 'mergeTag';
	value: string;
	label?: string;
}

// Parse text with {{...}} into parts for rendering
function parseTextWithMergeTags(text: string): TextPart[] {
	if (!text) return [];

	const parts: TextPart[] = [];
	const regex = /\{\{([^}]+)\}\}/g;
	let lastIndex = 0;
	let match;

	while ((match = regex.exec(text)) !== null) {
		// Add text before merge tag
		if (match.index > lastIndex) {
			const textBefore = text.substring(lastIndex, match.index);
			if (textBefore) {
				parts.push({ type: 'text', value: textBefore });
			}
		}

		// Add merge tag
		const mergeTagValue = match[1];
		parts.push({
			type: 'mergeTag',
			value: mergeTagValue,
			label: getMergeTagLabel(mergeTagValue) || mergeTagValue,
		});

		lastIndex = match.index + match[0].length;
	}

	// Add remaining text
	if (lastIndex < text.length) {
		const textAfter = text.substring(lastIndex);
		if (textAfter) {
			parts.push({ type: 'text', value: textAfter });
		}
	}

	return parts;
}

// Component to render text with styled merge tags
function RenderTextWithMergeTags({ text }: { text: string }) {
	const parts = parseTextWithMergeTags(text);

	if (parts.length === 0) return null;

	return (
		<>
			{parts.map((part, index) => {
				if (part.type === 'mergeTag') {
					return (
						<span
							key={`merge-${index}`}
							className={styles.mergeTag}
						>
							{part.label}
						</span>
					);
				}
				return <span key={`text-${index}`}>{part.value}</span>;
			})}
		</>
	);
}

export interface SubjectPreviewPanelProps {
	onSubjectPreviewSave?: (subject: string, previewText: string) => void;
	openFromModal?: boolean;
}

export function SubjectPreviewPanel({
	onSubjectPreviewSave,
	openFromModal = false,
}: SubjectPreviewPanelProps) {
	const [drawerVisible, setDrawerVisible] = useState(false);
	const formState = useFormState();

	const subject = formState.values?.subject || '';
	const subTitle = formState.values?.subTitle || '';

	const handleEditClick = () => {
		setDrawerVisible(true);
	};

	const handleCloseDrawer = () => {
		setDrawerVisible(false);
	};

	return (
		<>
			<div className={styles.previewPanel}>
				<div className={styles.previewContent}>
					<div className={styles.previewItem}>
						<span className={styles.previewLabel}>SUBJECT:</span>
						{subject ? (
							<span
								className={styles.previewText}
								title={subject}
							>
								<RenderTextWithMergeTags text={subject} />
							</span>
						) : (
							<span className={styles.previewEmpty}>No subject set</span>
						)}
					</div>
					<div className={styles.previewItem}>
						<span className={styles.previewLabel}>PREVIEW TEXT:</span>
						{subTitle ? (
							<span
								className={styles.previewText}
								title={subTitle}
							>
								<RenderTextWithMergeTags text={subTitle} />
							</span>
						) : (
							<span className={styles.previewEmpty}>No preview text set</span>
						)}
					</div>
				</div>
				<Tooltip content='Edit Subject and Preview Text' placement='top'>
					<button
						className={styles.editButton}
						onClick={handleEditClick}
					>
						{React.createElement(BsPencilFill as any)}
					</button>
				</Tooltip>
			</div>

			<SubjectPreviewDrawer
				visible={drawerVisible}
				onClose={handleCloseDrawer}
				onSave={onSubjectPreviewSave}
				openFromModal={openFromModal}
			/>
		</>
	);
}
